// Aba Financeiro do processo: mesmo formulário simples do cadastro de processo
// (cobrar ou pro bono, honorários iniciais e de êxito). Depois de salvo, mostra um
// resumo elegante com os lançamentos e um botão "Alterar" para voltar ao formulário.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, CheckCircle2, Clock, Heart, Loader2, Pencil, Save, Trophy, Undo2, Wallet } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SimpleFeeConfig } from '@/components/financial/SimpleFeeConfig';
import { createFinancialEntriesFromContract, saveFinancialEntries } from '@/utils/createFinancialEntries';
import { isClientCourtCost } from '@/utils/courtCosts';
import type { ContractConfiguration } from '@/types/ContractConfiguration';

interface Props {
  caseDetails: any;
  onSaved: () => void;
  /** Processo judicial (cases) ou administrativo (administrative_processes) */
  kind?: 'case' | 'administrative';
}

const brl = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const METHOD_LABELS: Record<string, string> = {
  pix: 'Pix',
  bank_transfer: 'Transferência',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  money: 'Dinheiro',
  boleto: 'Boleto',
};

const dateBR = (value: unknown) => {
  if (!value) return null;
  const raw = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const isPaid = (e: any) => String(e.status || '').toLowerCase() === 'pago';

export const CaseFinancialSettings = ({ caseDetails, onSaved, kind = 'case' }: Props) => {
  const isAdmin = kind === 'administrative';
  const { toast } = useToast();
  const navigate = useNavigate();
  const savedContract = ((isAdmin ? caseDetails.fee_configuration?.simpleContract : caseDetails.contractconfiguration) || null) as ContractConfiguration | null;
  const savedProBono = isAdmin ? !!(caseDetails.is_pro_bono || caseDetails.fee_configuration?.isProBono) : !!caseDetails.feeconfiguration?.isProBono;
  const hasSaved = savedProBono || !!savedContract;

  const [contract, setContract] = useState<ContractConfiguration | null>(savedContract);
  const [proBono, setProBono] = useState(savedProBono);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<any[]>([]);
  const [editing, setEditing] = useState(!hasSaved);
  // Recria o formulário ao cancelar (ele guarda estado interno)
  const [formKey, setFormKey] = useState(0);

  const loadEntries = async () => {
    const { data } = await supabase
      .from('financial_entries')
      .select('id, entry_type, amount, status, category, description, case_id, administrative_process_id, payment_date, due_date, actual_payment_date')
      .eq(isAdmin ? 'administrative_process_id' : 'case_id', caseDetails.id)
      .order('payment_date', { ascending: true, nullsFirst: false });
    // Custas pagas pelo cliente ficam no quadro próprio, acima
    setEntries((data || []).filter((e) => !isClientCourtCost(e)));
  };

  useEffect(() => {
    loadEntries();
  }, [caseDetails.id]);

  const dirty = proBono !== savedProBono || JSON.stringify(contract || null) !== JSON.stringify(savedContract || null);

  const totals = useMemo(() => {
    const receitas = entries.filter((e) => e.entry_type === 'receita');
    const sum = (list: any[]) => list.reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
    const paid = receitas.filter(isPaid);
    const open = receitas.filter((e) => !isPaid(e));
    return { paid: sum(paid), open: sum(open), paidCount: paid.length, openCount: open.length, receitas };
  }, [entries]);

  const startEditing = () => {
    setContract(savedContract);
    setProBono(savedProBono);
    setFormKey((k) => k + 1);
    setEditing(true);
  };

  const cancelEditing = () => {
    setContract(savedContract);
    setProBono(savedProBono);
    setFormKey((k) => k + 1);
    setEditing(false);
  };

  const handleSave = async () => {
    const paidFeeEntries = entries.filter(isPaid).length;
    const contractChanged = JSON.stringify(contract || null) !== JSON.stringify(savedContract || null);
    if (contractChanged && !proBono && paidFeeEntries > 0) {
      const ok = window.confirm(
        `Este processo já tem ${paidFeeEntries} lançamento(s) marcado(s) como pago.\n\nAo mudar os honorários, os lançamentos de honorários deste processo são refeitos a partir da nova configuração. Deseja continuar?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada. Entre novamente.');

      const { error } = isAdmin
        ? await (supabase as any)
            .from('administrative_processes')
            .update({
              fee_configuration: { ...(caseDetails.fee_configuration || {}), simpleContract: proBono ? null : contract, isProBono: proBono },
              is_pro_bono: proBono,
            })
            .eq('id', caseDetails.id)
        : await supabase
            .from('cases')
            .update({
              contractconfiguration: proBono ? null : contract,
              feeconfiguration: { ...(caseDetails.feeconfiguration || {}), isProBono: proBono },
            } as any)
            .eq('id', caseDetails.id);
      if (error) throw error;

      // Honorários mudaram: refaz os lançamentos de honorários (custas do cliente ficam intactas)
      let created = 0;
      if (contractChanged || proBono !== savedProBono) {
        const feeIds = entries.map((e) => e.id);
        if (feeIds.length > 0) {
          const { error: deleteError } = await supabase.from('financial_entries').delete().in('id', feeIds);
          if (deleteError) throw deleteError;
        }
        if (!proBono && contract) {
          const generated = await createFinancialEntriesFromContract(contract, caseDetails.id, caseDetails.client_id || null, user.id, 'monthly');
          // Processo administrativo: o vínculo é por administrative_process_id
          const newEntries = isAdmin
            ? generated.map((e) => ({ ...e, case_id: null, administrative_process_id: caseDetails.id } as any))
            : generated;
          if (newEntries.length > 0) {
            await saveFinancialEntries(newEntries);
            created = newEntries.length;
          }
        }
      }

      toast({
        title: 'Financeiro salvo',
        description: proBono
          ? 'Processo marcado como pro bono.'
          : created > 0
            ? `${created} lançamento(s) criado(s) no Financeiro.`
            : 'Configuração de honorários atualizada.',
      });
      await loadEntries();
      setEditing(false);
      onSaved();
    } catch (error: any) {
      console.error('Erro ao salvar financeiro do processo:', error);
      toast({ title: 'Erro ao salvar', description: error?.message || 'Tente novamente.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ---------- Marcar lançamento como pago ----------
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payDate, setPayDate] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [busyEntry, setBusyEntry] = useState<string | null>(null);

  const todayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const startPaying = (e: any) => {
    setPayingId(e.id);
    setPayDate(todayLocal());
    setPayAmount(Number(e.amount) ? Number(e.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '');
  };

  const confirmPaid = async (e: any) => {
    const amount = Number(String(payAmount).replace(/\./g, '').replace(',', '.'));
    if (!payDate) return;
    setBusyEntry(e.id);
    try {
      const { error } = await supabase
        .from('financial_entries')
        .update({
          status: 'Pago',
          payment_date: payDate,
          actual_payment_date: payDate,
          ...(amount > 0 ? { amount } : {}),
        } as any)
        .eq('id', e.id);
      if (error) throw error;
      toast({ title: 'Marcado como pago', description: `${e.description} · ${brl(amount > 0 ? amount : Number(e.amount))}` });
      setPayingId(null);
      await loadEntries();
    } catch (error: any) {
      toast({ title: 'Não foi possível marcar como pago', description: error?.message, variant: 'destructive' });
    } finally {
      setBusyEntry(null);
    }
  };

  const markPending = async (e: any) => {
    if (!window.confirm(`Voltar "${e.description}" para pendente?`)) return;
    setBusyEntry(e.id);
    try {
      const { error } = await supabase
        .from('financial_entries')
        .update({ status: 'Pendente', actual_payment_date: null } as any)
        .eq('id', e.id);
      if (error) throw error;
      await loadEntries();
    } catch (error: any) {
      toast({ title: 'Não foi possível alterar', description: error?.message, variant: 'destructive' });
    } finally {
      setBusyEntry(null);
    }
  };

  // ---------- Resumo do contrato salvo ----------
  const cc: any = savedContract || {};
  const hasInitial = !!cc.hasInitialFee && Number(cc.initialFeeAmount) > 0;
  const hasSuccess = cc.mode === 'success_fee';
  const installments = Math.max(1, Number(cc.initialFeeInstallments) || 1);

  const successLines = (() => {
    if (!hasSuccess) return null;
    if (cc.successType === 'benefit') {
      const each = ((cc.benefitPercentage ?? 0) / 100) * (cc.salaryValue || 0);
      return {
        main: `${cc.benefitPercentage ?? 0}% de cada parcela do benefício`,
        detail: `${cc.salaryCount || 0}x de ${brl(each)} · benefício de ${brl(cc.salaryValue || 0)}`,
        total: (cc.salaryCount || 0) * each,
      };
    }
    if (cc.successType === 'salaries') {
      return {
        main: `${cc.salaryCount || 0} salário${cc.salaryCount === 1 ? '' : 's'}`,
        detail: `Salário de ${brl(cc.salaryValue || 0)}`,
        total: (cc.salaryCount || 0) * (cc.salaryValue || 0),
      };
    }
    const base = cc.successBaseValue || 0;
    return {
      main: `${cc.successPercentage ?? 0}% ${base ? `sobre ${brl(base)}` : 'do resultado'}`,
      detail: base ? 'Sobre o valor da causa ou da condenação' : 'Valor definido no resultado do processo',
      total: ((cc.successPercentage ?? 0) / 100) * base,
    };
  })();

  const Summary = () => (
    <>
      {savedProBono ? (
        <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-r from-pink-50 to-[#faf7f2] p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-pink-500 shadow-sm">
            <Heart className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">Pro bono</p>
            <p className="text-sm text-slate-500">Este processo é atendido sem cobrança de honorários.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* Iniciais */}
          <div className={`rounded-2xl border p-4 ${hasInitial ? 'border-[#c4ab83]/40 bg-white' : 'border-dashed border-slate-200 bg-slate-50/50'}`}>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <Wallet className="h-3.5 w-3.5 text-[#c4ab83]" /> Honorários iniciais
            </div>
            {hasInitial ? (
              <>
                <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">{brl(cc.initialFeeAmount)}</p>
                <p className="text-sm text-slate-600">
                  {installments > 1 ? `${installments}x de ${brl(cc.initialFeeAmount / installments)}` : 'À vista'}
                  {cc.initialFeePaymentMethod && ` · ${METHOD_LABELS[cc.initialFeePaymentMethod] || cc.initialFeePaymentMethod}`}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  {dateBR(cc.initialFeeFirstPaymentDate) && (
                    <span className="text-slate-500">{installments > 1 ? '1ª parcela em ' : 'Em '}{dateBR(cc.initialFeeFirstPaymentDate)}</span>
                  )}
                  {cc.initialFeeAlreadyPaid ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> {installments > 1 ? '1ª paga' : 'Pago'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                      <Clock className="h-3 w-3" /> A receber
                    </span>
                  )}
                </div>
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-400">Sem honorários iniciais.</p>
            )}
          </div>

          {/* Êxito */}
          <div className={`rounded-2xl border p-4 ${successLines ? 'border-[#c4ab83]/40 bg-white' : 'border-dashed border-slate-200 bg-slate-50/50'}`}>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <Trophy className="h-3.5 w-3.5 text-[#c4ab83]" /> Honorários de êxito
            </div>
            {successLines ? (
              <>
                <p className={`mt-2 text-2xl font-semibold tabular-nums tracking-tight ${successLines.total ? 'text-slate-900' : 'text-slate-300'}`}>
                  {successLines.total ? brl(successLines.total) : 'A definir'}
                </p>
                <p className="text-sm text-slate-600">{successLines.main}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500">{successLines.detail}</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                    <Clock className="h-3 w-3" /> Até o resultado
                  </span>
                </div>
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-400">Sem honorários de êxito.</p>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Honorários</p>
            <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">
              {editing ? 'Como este processo é cobrado' : 'Contrato de honorários'}
            </p>
            {caseDetails.case_value ? (
              <p className="mt-0.5 text-xs text-slate-500">Valor da causa: <span className="font-semibold text-slate-700">{brl(Number(caseDetails.case_value))}</span></p>
            ) : null}
          </div>
          {!editing && hasSaved && (
            <button
              type="button"
              onClick={startEditing}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 transition-colors hover:border-[#c4ab83] hover:bg-[#faf7f2] hover:text-[#7a6440]"
            >
              <Pencil className="h-3.5 w-3.5" /> Alterar
            </button>
          )}
        </div>

        {editing ? (
          <>
            <SimpleFeeConfig
              key={formKey}
              caseValue={Number(caseDetails.case_value) || 0}
              contractConfiguration={contract}
              onContractConfigurationChange={setContract}
              isProBono={proBono}
              onProBonoChange={(value) => {
                setProBono(value);
                if (value) setContract(null);
              }}
            />
            <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-400">Ao salvar, os lançamentos de honorários são atualizados no Financeiro.</p>
              <div className="flex gap-2">
                {hasSaved && (
                  <Button variant="outline" onClick={cancelEditing} disabled={saving} className="rounded-xl">Cancelar</Button>
                )}
                <Button onClick={handleSave} disabled={!dirty || saving} className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Salvar financeiro
                </Button>
              </div>
            </div>
          </>
        ) : (
          <Summary />
        )}
      </section>

      {/* Lançamentos deste processo */}
      <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <p className="text-sm font-semibold text-slate-800">Lançamentos no Financeiro</p>
          <button type="button" onClick={() => navigate('/financial')} className="flex items-center gap-1 text-xs font-semibold text-[#9a7f52] hover:underline">
            Abrir Financeiro <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-px bg-slate-100">
          <div className="bg-white px-5 py-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Recebido</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${totals.paid ? 'text-emerald-600' : 'text-slate-300'}`}>{brl(totals.paid)}</p>
            <p className="text-[11px] text-slate-400">{totals.paidCount} lançamento(s)</p>
          </div>
          <div className="bg-white px-5 py-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">A receber</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${totals.open ? 'text-slate-900' : 'text-slate-300'}`}>{brl(totals.open)}</p>
            <p className="text-[11px] text-slate-400">{totals.openCount} lançamento(s)</p>
          </div>
        </div>
        {totals.receitas.length > 0 && (
          <ul className="divide-y divide-slate-100 border-t border-slate-100">
            {totals.receitas.map((e) => (
              <li key={e.id} className="group px-5 py-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-slate-700">{e.description}</p>
                    <p className="text-xs text-slate-400">
                      {isPaid(e) && e.actual_payment_date ? `Recebido em ${dateBR(e.actual_payment_date)}` : dateBR(e.payment_date || e.due_date) || 'Sem data'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={`font-semibold tabular-nums ${Number(e.amount) ? 'text-slate-900' : 'text-slate-300'}`}>{Number(e.amount) ? brl(Number(e.amount)) : 'A definir'}</span>
                    {isPaid(e) ? (
                      <span className="flex items-center gap-1">
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Pago</span>
                        <button
                          type="button"
                          onClick={() => markPending(e)}
                          disabled={busyEntry === e.id}
                          className="rounded-md p-1 text-slate-300 opacity-100 transition-opacity hover:bg-slate-100 hover:text-slate-600 md:opacity-0 md:group-hover:opacity-100"
                          title="Voltar para pendente"
                        >
                          <Undo2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ) : payingId === e.id ? null : (
                      <button
                        type="button"
                        onClick={() => startPaying(e)}
                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Marcar como pago
                      </button>
                    )}
                  </div>
                </div>

                {payingId === e.id && (
                  <div className="mt-2 flex flex-col gap-2 rounded-xl bg-emerald-50/60 p-3 sm:flex-row sm:items-end">
                    <label className="flex-1 text-xs text-slate-600">
                      Recebido em
                      <input
                        type="date"
                        value={payDate}
                        onChange={(ev) => setPayDate(ev.target.value)}
                        className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <label className="flex-1 text-xs text-slate-600">
                      Valor recebido (R$)
                      <input
                        type="text"
                        inputMode="decimal"
                        value={payAmount}
                        onChange={(ev) => setPayAmount(ev.target.value)}
                        className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm tabular-nums text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => setPayingId(null)} className="h-9 rounded-lg text-slate-600">Cancelar</Button>
                      <Button size="sm" onClick={() => confirmPaid(e)} disabled={busyEntry === e.id || !payDate} className="h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
                        {busyEntry === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirmar'}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
